import { FC, useState, ChangeEvent } from 'react';
import { Select, Box, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from 'store/admin/hooks';
import { ApplicationStatus } from 'store/applications/types';
import { appStatusOptions } from 'core/constants';
import useStyles from './MultiStatusUpdate.styles';

interface MultiStatusUpdateProps {
  applicationIds: string[]; // Multiple applications selected for status update
}

const MultiStatusUpdate: FC<MultiStatusUpdateProps> = ({ applicationIds }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess, loading, updateApplicationStatus, rejectApplication } = useAdmin();

  const [status, setStatus] = useState<string>('');

  const onStatusChange = async (event: ChangeEvent<{ value: unknown }>) => {
    const selectedStatus = event.target.value as ApplicationStatus;
    setStatus(selectedStatus);

    if (!selectedStatus) {
      setError(t('No status selected'));
      return;
    }
    if (applicationIds.length === 0) {
      setError(t('No applications selected'));
      return;
    }

    try {
      if (selectedStatus === ApplicationStatus.REJECTED) {
        // Assumption: rejectApplication accepts single id, so reject each separately
        await Promise.all(applicationIds.map((id) => rejectApplication({ id })));
      } else {
        // Batch update all applications' status
        await Promise.all(applicationIds.map((id) => updateApplicationStatus({ id, appStatus: selectedStatus })));
      }
      setSuccess(t('Applications status updated successfully'));
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errMessage = (err as any)?.response?.message;
      setError(errMessage || (err as Error).message);
    }
  };

  return (
    <Box className={classes.multiStatusContainer}>
      <Typography>Update {t('pages.lead.sections.header.status')}</Typography>
      <Select
        native
        IconComponent={() => <ExpandMore className={classes.expandIcon} />}
        variant="outlined"
        onChange={onStatusChange}
        value={status}
        color="primary"
        className={classes.select}
        disabled={loading}
        displayEmpty
        inputProps={{ 'aria-label': t('Select status') }}
      >
        <option value="">{t('None')}</option>
        {appStatusOptions.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
    </Box>
  );
};

export default MultiStatusUpdate;
